import "@/App.css";
import { BrowserRouter, Link, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { PublicOnly, RequireSignedIn, RequireVerified } from "@/components/RouteGuards";
import Landing from "@/pages/Landing";
import SignUp from "@/pages/auth/SignUp";
import SignIn from "@/pages/auth/SignIn";
import CheckEmail from "@/pages/auth/CheckEmail";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AppHome from "@/pages/app/AppHome";
import NewWorkspace from "@/pages/app/NewWorkspace";
import AppShell from "@/components/app/AppShell";
import Overview from "@/pages/app/Overview";
import Documents from "@/pages/app/Documents";
import Actions from "@/pages/app/Actions";
import Deadlines from "@/pages/app/Deadlines";
import Settings from "@/pages/app/Settings";
import Properties from "@/pages/app/Properties";
import Forms from "@/pages/app/Forms";
import FormDetail from "@/pages/app/FormDetail";
import DraftEditor from "@/pages/app/DraftEditor";
import IpAssets from "@/pages/app/IpAssets";
import IpAssetDetail from "@/pages/app/IpAssetDetail";
import Billing from "@/pages/app/Billing";
import Affiliate from "@/pages/app/Affiliate";
import Admin from "@/pages/app/Admin";
import Compliance from "@/pages/app/Compliance";
import PaymentResult from "@/pages/PaymentResult";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { attributeRef, captureRef } from "@/lib/referral";

const RefTracker = () => {
  const { user } = useAuth();
  useEffect(() => { captureRef(); }, []);
  useEffect(() => { if (user?.email_verified) attributeRef(); }, [user]);
  return null;
};

const NotFound = () => (
  <div data-testid="not-found-page" className="grid min-h-screen place-items-center bg-paper px-6 text-center">
    <div>
      <p className="eyebrow text-gold-dark">Page not found</p>
      <h1 className="mt-3 text-3xl font-bold text-navy">We couldn't find that page.</h1>
      <Link to="/" data-testid="not-found-home-link" className="mt-6 inline-block text-navy underline underline-offset-4">Go to the home page</Link>
    </div>
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <RefTracker />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/signup" element={<PublicOnly><SignUp /></PublicOnly>} />
            <Route path="/signin" element={<PublicOnly><SignIn /></PublicOnly>} />
            <Route path="/check-email" element={<RequireSignedIn><CheckEmail /></RequireSignedIn>} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/app" element={<RequireVerified><AppHome /></RequireVerified>} />
            <Route path="/app/new-workspace" element={<RequireVerified><NewWorkspace /></RequireVerified>} />
            <Route path="/app/w/:wid" element={<RequireVerified><AppShell /></RequireVerified>}>
              <Route index element={<Overview />} />
              <Route path="documents" element={<Documents />} />
              <Route path="actions" element={<Actions />} />
              <Route path="deadlines" element={<Deadlines />} />
              <Route path="settings" element={<Settings />} />
              <Route path="properties" element={<Properties />} />
              <Route path="forms" element={<Forms />} />
              <Route path="forms/drafts/:draftId" element={<DraftEditor />} />
              <Route path="forms/:formId" element={<FormDetail />} />
              <Route path="ip" element={<IpAssets />} />
              <Route path="ip/:assetId" element={<IpAssetDetail />} />
              <Route path="billing" element={<Billing />} />
              <Route path="affiliate" element={<Affiliate />} />
              <Route path="admin" element={<Admin />} />
              <Route path="compliance" element={<Compliance />} />
            </Route>
            <Route path="/payment/success" element={<RequireSignedIn><PaymentResult /></RequireSignedIn>} />
            <Route path="/payment/cancel" element={<RequireSignedIn><PaymentResult cancelled /></RequireSignedIn>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
